import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import EndpointsEnum from "@models/EndpointsEnum";
import ISerial from "@models/ISerial";
import IPO_Shipment_Catalog_Item_MapUI from "@models/IPO_Shipment_Catalog_Item_MapUI";
import IProcessPOShipmentItemSerialsDTO from "@models/DTO/IProcessPOShipmentItemSerialsDTO";
import IOnProcessPOShipmentItemSerialsDTO from "@models/DTO/IOnProcessPOShipmentItemSerialsDTO";
import IValidateSerialsDTO from "@models/DTO/IValidateSerialsDTO";
import IOnValidateSerialsDTO from "@models/DTO/IOnValidateSerialsDTO";
import UiContext from "@contexts/UiContext";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import SerialService from "@services/SerialService";

export interface IPendingSerial {
    value: string;
    isValid: boolean | null; // null = validating, true = valid, false = invalid
    error?: string;
}

export interface ISerialEntryItemState {
    serials: ISerial[];
    pendingSerials: IPendingSerial[];
    errors: string[];
    isSubmitting: boolean;
}

export interface ISerialEntryState {
    [catalogItemId: number]: ISerialEntryItemState;
}

const defaultItemState: ISerialEntryItemState = {
    serials: [],
    pendingSerials: [],
    errors: [],
    isSubmitting: false,
};

interface UsePurchaseOrderShipmentSerialsProps {
    poShipmentGuid: string;
}

export const usePurchaseOrderShipmentSerials = ({ poShipmentGuid }: UsePurchaseOrderShipmentSerialsProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const [serialState, setSerialState] = useState<ISerialEntryState>({});
    const submitTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
    // Each outstanding entry tracks the serial values the flush must persist for
    // that item, so a response to a pre-flush submission can't resolve the flush
    // while newer serials are still pending.
    const flushRef = useRef<{
        outstanding: Map<number, { mapId: number; values: Set<string> }>;
        failed: boolean;
        resolve: (ok: boolean) => void;
    } | null>(null);

    const initializeSerials = useCallback((items: IPO_Shipment_Catalog_Item_MapUI[]) => {
        setSerialState((prev) => {
            const next: ISerialEntryState = { ...prev };
            let changed = false;

            for (const item of items) {
                const existing = prev[item.catalog_item_id];
                const serverSerials = item.Serials || [];
                if (existing || serverSerials.length === 0) continue;

                next[item.catalog_item_id] = {
                    ...defaultItemState,
                    serials: serverSerials,
                };
                changed = true;
            }

            return changed ? next : prev;
        });
    }, []);

    const getItemState = useCallback(
        (catalogItemId: number): ISerialEntryItemState => {
            return serialState[catalogItemId] || defaultItemState;
        },
        [serialState]
    );

    const addPendingSerial = useCallback(
        (catalogItemId: number, value: string) => {
            const trimmed = value.trim();
            if (!trimmed) return;

            setSerialState((prev) => {
                const itemState = prev[catalogItemId] || defaultItemState;

                // Check for local duplicates against pending and created serials
                const existingPending = itemState.pendingSerials.some((p) => p.value === trimmed);
                const existingCreated = itemState.serials.some((s) => s.value === trimmed);
                if (existingPending || existingCreated) return prev;

                return {
                    ...prev,
                    [catalogItemId]: {
                        ...itemState,
                        pendingSerials: [...itemState.pendingSerials, { value: trimmed, isValid: null }],
                    },
                };
            });

            // Emit validation request to server
            const payload: IValidateSerialsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                serials: [trimmed],
            };
            socket.emit(EndpointsEnum.SerialValidateSerials, payload);
        },
        [userModel.guid, userModel.token, supplierModel.guid, socket]
    );

    const addBulkPendingSerials = useCallback((catalogItemId: number, values: string[]) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId] || defaultItemState;
            const existingValues = new Set([...itemState.pendingSerials.map((p) => p.value), ...itemState.serials.map((s) => s.value)]);

            const newPending: IPendingSerial[] = values.filter((v) => !existingValues.has(v)).map((value) => ({ value, isValid: true }));

            if (newPending.length === 0) return prev;

            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    pendingSerials: [...itemState.pendingSerials, ...newPending],
                },
            };
        });
    }, []);

    const removePendingSerial = useCallback((catalogItemId: number, index: number) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId] || defaultItemState;
            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    pendingSerials: itemState.pendingSerials.filter((_, i) => i !== index),
                },
            };
        });
    }, []);

    // Fallback: reset isSubmitting if no server response within 10s
    const startSubmitTimeout = useCallback((catalogItemId: number) => {
        const existingTimeout = submitTimeoutsRef.current.get(catalogItemId);
        if (existingTimeout) clearTimeout(existingTimeout);

        const timeoutId = setTimeout(() => {
            submitTimeoutsRef.current.delete(catalogItemId);
            setSerialState((prev) => {
                const itemState = prev[catalogItemId];
                if (itemState?.isSubmitting) {
                    return {
                        ...prev,
                        [catalogItemId]: {
                            ...itemState,
                            isSubmitting: false,
                            errors: [...itemState.errors, "Request timed out. Please try again."],
                        },
                    };
                }
                return prev;
            });

            const flush = flushRef.current;
            if (flush && flush.outstanding.has(catalogItemId)) {
                flushRef.current = null;
                flush.resolve(false);
            }
        }, 10000);
        submitTimeoutsRef.current.set(catalogItemId, timeoutId);
    }, []);

    const submitSerials = useCallback(
        (catalogItemId: number, catalogItemMapId: number) => {
            const currentState = serialState[catalogItemId] || defaultItemState;
            const validSerials = currentState.pendingSerials.filter((p) => p.isValid === true);
            if (validSerials.length === 0 || currentState.isSubmitting) return;

            const payload: IProcessPOShipmentItemSerialsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                poShipmentGuid,
                catalogItemId,
                catalogItemMapId,
                serials: validSerials.map((p) => p.value),
            };

            setSerialState((prev) => ({
                ...prev,
                [catalogItemId]: {
                    ...(prev[catalogItemId] || defaultItemState),
                    isSubmitting: true,
                    errors: [],
                },
            }));

            socket.emit(EndpointsEnum.PurchaseOrderProcessPOShipmentItemSerials, payload);

            startSubmitTimeout(catalogItemId);
        },
        [serialState, userModel.guid, userModel.token, supplierModel.guid, poShipmentGuid, socket, startSubmitTimeout]
    );

    /**
     * Save every item's pending serials before finalizing. Resolves true only
     * when all pending serials are persisted; resolves false (with a
     * notification) when any are invalid, still validating, or rejected by the
     * server — the caller must not finalize in that case.
     */
    const flushPendingSerials = useCallback(
        (items: IPO_Shipment_Catalog_Item_MapUI[]): Promise<boolean> => {
            const itemsWithPending = items.filter((item) => (serialState[item.catalog_item_id]?.pendingSerials.length || 0) > 0);
            if (itemsWithPending.length === 0) return Promise.resolve(true);

            for (const item of itemsWithPending) {
                const pending = serialState[item.catalog_item_id].pendingSerials;
                const itemName = item.Catalog_Item?.name || "Item";

                if (pending.some((p) => p.isValid === false)) {
                    uiContext.showNotification({
                        message: `${itemName}: fix or remove the invalid serials before finalizing`,
                        level: NotificationLevelEnum.error,
                    });
                    return Promise.resolve(false);
                }

                if (pending.some((p) => p.isValid === null)) {
                    uiContext.showNotification({
                        message: `${itemName}: serials are still validating — try again in a moment`,
                        level: NotificationLevelEnum.error,
                    });
                    return Promise.resolve(false);
                }
            }

            return new Promise<boolean>((resolve) => {
                flushRef.current = {
                    // All pending serials are valid at this point (checked above).
                    outstanding: new Map(
                        itemsWithPending.map((item) => [
                            item.catalog_item_id,
                            {
                                mapId: item.id,
                                values: new Set(serialState[item.catalog_item_id].pendingSerials.map((p) => p.value)),
                            },
                        ])
                    ),
                    failed: false,
                    resolve,
                };

                for (const item of itemsWithPending) {
                    submitSerials(item.catalog_item_id, item.id);
                }
            });
        },
        [serialState, submitSerials, uiContext]
    );

    const deleteSerial = useCallback(
        async (catalogItemId: number, serialId: number) => {
            const itemState = serialState[catalogItemId] || defaultItemState;
            const serial = itemState.serials.find((s) => s.id === serialId);
            if (!serial) return;

            try {
                await SerialService.deleteSerial(serialId, serial.value, userModel, supplierModel);

                uiContext.showNotification({
                    message: `Serial ${serial.value} deleted`,
                    level: NotificationLevelEnum.success,
                });

                setSerialState((prev) => {
                    const currentItemState = prev[catalogItemId] || defaultItemState;
                    return {
                        ...prev,
                        [catalogItemId]: {
                            ...currentItemState,
                            serials: currentItemState.serials.filter((s) => s.id !== serialId),
                        },
                    };
                });
            } catch (error: any) {
                setSerialState((prev) => {
                    const currentItemState = prev[catalogItemId] || defaultItemState;
                    return {
                        ...prev,
                        [catalogItemId]: {
                            ...currentItemState,
                            errors: [...currentItemState.errors, error.message || "Failed to delete serial"],
                        },
                    };
                });
            }
        },
        [serialState, userModel, supplierModel, uiContext]
    );

    const clearErrors = useCallback((catalogItemId: number) => {
        setSerialState((prev) => {
            const itemState = prev[catalogItemId];
            if (!itemState) return prev;
            return {
                ...prev,
                [catalogItemId]: {
                    ...itemState,
                    errors: [],
                },
            };
        });
    }, []);

    useEffect(() => {
        const onProcessPOShipmentItemSerials = (data: IOnProcessPOShipmentItemSerialsDTO) => {
            const catalogItemId = data.catalogItemId;

            const existingTimeout = submitTimeoutsRef.current.get(catalogItemId);
            if (existingTimeout) {
                clearTimeout(existingTimeout);
                submitTimeoutsRef.current.delete(catalogItemId);
            }

            setSerialState((prev) => {
                const itemState = prev[catalogItemId] || defaultItemState;

                const createdValues = new Set(data.serials.map((s) => s.value));
                const remainingPending = itemState.pendingSerials.filter((p) => !createdValues.has(p.value));

                return {
                    ...prev,
                    [catalogItemId]: {
                        serials: [...itemState.serials, ...data.serials],
                        pendingSerials: remainingPending,
                        errors: data.errors || [],
                        isSubmitting: false,
                    },
                };
            });

            const flush = flushRef.current;
            const flushEntry = flush?.outstanding.get(catalogItemId);
            if (flush && flushEntry) {
                if (data.errors && data.errors.length > 0) flush.failed = true;

                for (const serial of data.serials) {
                    flushEntry.values.delete(serial.value);
                }

                if (flushEntry.values.size > 0 && !flush.failed) {
                    // This response belongs to a submission started before the
                    // flush; submit the flush's remaining serials now and keep
                    // the item outstanding until they come back.
                    const payload: IProcessPOShipmentItemSerialsDTO = {
                        userGuid: userModel.guid,
                        userToken: userModel.token,
                        supplierGuid: supplierModel.guid,
                        poShipmentGuid,
                        catalogItemId,
                        catalogItemMapId: flushEntry.mapId,
                        serials: [...flushEntry.values],
                    };

                    setSerialState((prev) => ({
                        ...prev,
                        [catalogItemId]: {
                            ...(prev[catalogItemId] || defaultItemState),
                            isSubmitting: true,
                            errors: [],
                        },
                    }));

                    socket.emit(EndpointsEnum.PurchaseOrderProcessPOShipmentItemSerials, payload);
                    startSubmitTimeout(catalogItemId);
                    return;
                }

                flush.outstanding.delete(catalogItemId);
                if (flush.outstanding.size === 0) {
                    flushRef.current = null;
                    flush.resolve(!flush.failed);
                }
            }
        };

        const onValidateSerials = (data: IOnValidateSerialsDTO) => {
            setSerialState((prev) => {
                const next = { ...prev };
                let changed = false;

                for (const result of data.results) {
                    // Find which catalog item has this pending serial
                    for (const catalogItemId of Object.keys(next)) {
                        const id = Number(catalogItemId);
                        const itemState = next[id];
                        if (!itemState) continue;

                        const pendingIndex = itemState.pendingSerials.findIndex((p) => p.value === result.serial && p.isValid === null);

                        if (pendingIndex !== -1) {
                            const updatedPending = [...itemState.pendingSerials];
                            updatedPending[pendingIndex] = {
                                ...updatedPending[pendingIndex],
                                isValid: result.isValid,
                                error: result.error,
                            };
                            next[id] = {
                                ...itemState,
                                pendingSerials: updatedPending,
                            };
                            changed = true;
                            break;
                        }
                    }
                }

                return changed ? next : prev;
            });
        };

        socket.on(EndpointsEnum.PurchaseOrderOnProcessPOShipmentItemSerials, onProcessPOShipmentItemSerials);
        socket.on(EndpointsEnum.SerialOnValidateSerials, onValidateSerials);

        return () => {
            socket.off(EndpointsEnum.PurchaseOrderOnProcessPOShipmentItemSerials, onProcessPOShipmentItemSerials);
            socket.off(EndpointsEnum.SerialOnValidateSerials, onValidateSerials);
        };
    }, [socket, userModel.guid, userModel.token, supplierModel.guid, poShipmentGuid, startSubmitTimeout]);

    useEffect(() => {
        const timeouts = submitTimeoutsRef.current;
        return () => {
            timeouts.forEach(clearTimeout);
            timeouts.clear();
        };
    }, []);

    return {
        serialState,
        initializeSerials,
        getItemState,
        addPendingSerial,
        addBulkPendingSerials,
        removePendingSerial,
        submitSerials,
        flushPendingSerials,
        deleteSerial,
        clearErrors,
    };
};
